import { ReactNode, createContext, useCallback, useContext, useMemo, useState } from 'react';
import { useInView } from 'react-intersection-observer';
import { useMediaQuery } from '@givebutter/react-util';
import {
    IRecommendedActionWithState,
    useRecommendedActionsData,
} from 'components/recommended-actions/useRecommendedActionsData';
import { useAccountLocalStorage } from 'contexts/useAccountLocalStorage';

interface IRecommendedActionsContext {
    visibleActions: IRecommendedActionWithState[];
    containerRef: (node?: Element | null) => void;
    containerWasInFrame: boolean;
    showCompletionPopup: boolean;
    onClearAnimation: () => void;
}

const RecommendedActionsContext = createContext<IRecommendedActionsContext | undefined>(undefined);

const useRecommendedActionsContext = () => {
    const context = useContext(RecommendedActionsContext);
    if (!context) {
        throw new Error(
            'useRecommendedActionsContext must be used within a RecommendedActionsProvider',
        );
    }
    return context;
};

const RecommendedActionsProvider = ({ children }: { children: ReactNode }) => {
    const [containerWasInFrame, setContainerWasInFrame] = useState(false);
    const [clearedAnimation, setClearedAnimation] = useState(false);
    const { actionData } = useRecommendedActionsData();
    const isMobile = useMediaQuery(`(max-width: 640px)`);

    const { ref: containerRef } = useInView({
        threshold: isMobile ? 0.4 : 1,
        onChange: inView => {
            if (inView) {
                setContainerWasInFrame(true);
            }
        },
    });

    const { hiddenRecommendedActionsKeys: hiddenKeys, hasSeenCompleteAllRecommendedActionsDialog } =
        useAccountLocalStorage(
            ({ hiddenRecommendedActionsKeys, hasSeenCompleteAllRecommendedActionsDialog }) => ({
                hiddenRecommendedActionsKeys,
                hasSeenCompleteAllRecommendedActionsDialog,
            }),
        );

    const visibleActions = useMemo(() => {
        const filtered = actionData.filter(action => !hiddenKeys.includes(action.actionKey));
        return filtered;
    }, [actionData, hiddenKeys]);

    const showCompletionPopup = useMemo(() => {
        return (
            !hasSeenCompleteAllRecommendedActionsDialog &&
            clearedAnimation &&
            visibleActions.length === 0
        );
    }, [visibleActions.length, hasSeenCompleteAllRecommendedActionsDialog, clearedAnimation]);

    const onClearAnimation = useCallback(() => {
        setClearedAnimation(true);
    }, [setClearedAnimation]);

    const value = useMemo(
        () => ({
            visibleActions,
            containerRef,
            containerWasInFrame,
            showCompletionPopup,
            onClearAnimation,
        }),
        [visibleActions, containerRef, containerWasInFrame, showCompletionPopup, onClearAnimation],
    );
    return (
        <RecommendedActionsContext.Provider value={value}>
            {children}
        </RecommendedActionsContext.Provider>
    );
};

export { useRecommendedActionsContext, RecommendedActionsProvider };
